import "./css/storeinfo.css";
import StepButton from "../register/component/StepButton";
import { useState, useMemo, useEffect } from "react";
import {
  validateStoreInfo,
  restrictBusinessName,
  restrictPhone,
  formatPhone,
} from "../../utils/registerValidation";
import DaumPostcode from "react-daum-postcode";

const StoreInfo = ({ formData, setFormData, next, prev }) => {
  const [searchOpen, setSearchOpen] = useState(false);

  const [openDays, setOpenDays] = useState({
    MON: false,
    TUE: false,
    WED: false,
    THU: false,
    FRI: false,
    SAT: false,
    SUN: false,
  });

  // formData에서 영업일 복원
  useEffect(() => {
    if (formData.storeOpenDate && formData.storeOpenDate.length > 0) {
      const restoredDays = {
        MON: false,
        TUE: false,
        WED: false,
        THU: false,
        FRI: false,
        SAT: false,
        SUN: false,
      };

      formData.storeOpenDate.forEach((item) => {
        if (item.dayNm) {
          restoredDays[item.dayNm] = true;
        }
      });

      setOpenDays(restoredDays);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const time = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
        times.push(time);
      }
    }
    return times;
  };

  const timeOptions = useMemo(() => generateTimeOptions(), []);

  const closeTimeOptions = useMemo(() => {
    if (!formData.storeOpenTime) return [];
    const startIndex = timeOptions.indexOf(formData.storeOpenTime);
    return timeOptions.slice(startIndex + 1);
  }, [formData.storeOpenTime, timeOptions]);

  const handleInputChange = (field, value) => {
    let restrictedValue = value;

    if (field === "storeNm") {
      restrictedValue = restrictBusinessName(value);
    } else if (field === "storeTelNo") {
      restrictedValue = restrictPhone(value);
      restrictedValue = formatPhone(restrictedValue);
    }

    setFormData({
      ...formData,
      [field]: restrictedValue,
    });
  };

  const handleAddressSearch = () => {
    setSearchOpen(true);
  };

  const handleSelectAllDays = () => {
    const allSelected = Object.values(openDays).every((v) => v);
    const newOpenDays = Object.keys(openDays).reduce(
      (acc, key) => ({ ...acc, [key]: !allSelected }),
      {},
    );
    setOpenDays(newOpenDays);
    const storeOpenDate = allSelected
      ? []
      : Object.keys(newOpenDays).map((day) => ({ dayNm: day }));
    setFormData({ ...formData, storeOpenDate });
  };

  const handleOpenDayChange = (day) => {
    const newOpenDays = {
      ...openDays,
      [day]: !openDays[day],
    };
    setOpenDays(newOpenDays);

    const storeOpenDate = Object.keys(newOpenDays)
      .filter((key) => newOpenDays[key])
      .map((day) => ({ dayNm: day }));

    setFormData({
      ...formData,
      storeOpenDate: storeOpenDate,
    });
  };

  const handleNext = () => {
    const validation = validateStoreInfo(formData);
    if (!validation.isValid) {
      alert(validation.message);
      return;
    }

    next();
  };

  const handleComplete = (data) => {
    // 지번 주소로 저장 (통계 집계 시 행정동 파싱에 지번 형식이 필요함)
    const fullAddress = data.jibunAddress || data.autoJibunAddress || data.address;
    // 좌표 조회 실패 시 폴백용 도로명 주소
    const roadAddress = data.roadAddress || data.autoRoadAddress || data.address;

    setFormData({
      ...formData,
      storeZipCode: data.zonecode,
      storeAddr: fullAddress,
      storeRoadAddr: roadAddress,
    });
    setSearchOpen(false);
  };

  return (
    <div className="storeinfo-container">
      <div className="storeinfo-wrap">
        <div className="storeinfo-header">
          <div className="storeinfo-header-title">회원가입</div>
          <div className="storeinfo-header-text">2/4</div>
          <progress className="storeinfo-progress" value="2" max="4">
            50%
          </progress>
        </div>

        <div className="storeinfo-middle-title">가게 정보 입력</div>
        <div className="storeinfo-middle-content">
          <div className="storeinfo-middle-subtitle">가게명</div>
          <div>
            <input
              type="text"
              className="storeinfo-middle-nm-input"
              value={formData.storeNm}
              onChange={(e) => handleInputChange("storeNm", e.target.value)}
            />
          </div>

          <div className="storeinfo-middle-subtitle">가게 연락처</div>
          <div>
            <input
              type="text"
              className="storeinfo-middle-tel-input"
              placeholder="숫자만 입력 가능"
              value={formData.storeTelNo}
              onChange={(e) => handleInputChange("storeTelNo", e.target.value)}
            />
          </div>

          <div className="storeinfo-middle-subtitle">우편번호</div>
          <div className="storeinfo-middle-addr-div">
            <input
              type="text"
              className="storeinfo-middle-zipcode-input"
              disabled
              value={formData.storeZipCode}
            />
            <div
              className="storeinfo-middle-addr-search"
              onClick={handleAddressSearch}
            >
              주소검색
            </div>
          </div>
          <div className="storeinfo-middle-subtitle">주소</div>
          <div className="storeinfo-middle-addr-div">
            <input
              type="text"
              className="storeinfo-middle-addr-input"
              disabled
              value={formData.storeAddr}
            />
          </div>
          <div className="storeinfo-middle-subtitle">상세주소</div>
          <div>
            <input
              type="text"
              className="storeinfo-middle-addr-input"
              value={formData.storeAddrDetail}
              onChange={(e) =>
                handleInputChange("storeAddrDetail", e.target.value)
              }
            />
          </div>

          <div className="storeinfo-openday-header">
            <div className="storeinfo-middle-subtitle" style={{ margin: 0 }}>영업일</div>
            <button
              type="button"
              className="storeinfo-openday-allbtn"
              onClick={handleSelectAllDays}
            >
              {Object.values(openDays).every((v) => v)
                ? "전체 해제"
                : "전체 선택"}
            </button>
          </div>

          <div className="storeinfo-middle-check-div">
            {[
              ["MON", "월"],
              ["TUE", "화"],
              ["WED", "수"],
              ["THU", "목"],
              ["FRI", "금"],
              ["SAT", "토"],
              ["SUN", "일"],
            ].map(([key, label]) => (
              <label
                key={key}
                className={`storeinfo-openday-label${openDays[key] ? " checked" : ""}`}
              >
                <input
                  type="checkbox"
                  name={key}
                  checked={openDays[key]}
                  onChange={() => handleOpenDayChange(key)}
                />
                {label}
              </label>
            ))}
          </div>
          <div className="storeinfo-middle-subtitle">영업시간</div>
          <div className="storeinfo-middle-workhour-div">
            <div className="storeinfo-middle-work">
              <select
                className="storeinfo-middle-workhour-input"
                value={formData.storeOpenTime || ""}
                onChange={(e) => {
                  const newOpenTime = e.target.value;
                  const shouldResetCloseTime =
                    formData.storeCloseTime &&
                    newOpenTime >= formData.storeCloseTime;

                  setFormData({
                    ...formData,
                    storeOpenTime: newOpenTime,
                    storeCloseTime: shouldResetCloseTime
                      ? ""
                      : formData.storeCloseTime,
                  });
                }}
              >
                <option value="">시작 시간</option>
                {timeOptions.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
            <div className="storeinfo-middle-work-wave">~</div>
            <div className="storeinfo-middle-work">
              <select
                className="storeinfo-middle-workhour-input"
                value={formData.storeCloseTime || ""}
                onChange={(e) =>
                  handleInputChange("storeCloseTime", e.target.value)
                }
                disabled={!formData.storeOpenTime}
              >
                <option value="">종료 시간</option>
                {closeTimeOptions.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <StepButton next={handleNext} prev={prev} />
      </div>

      {searchOpen && (
        <div
          className="storeinfo-modal-overlay"
          onClick={() => setSearchOpen(false)}
        >
          <div
            className="storeinfo-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="storeinfo-modal-header">
              <h3>주소 검색</h3>
              <button
                className="storeinfo-modal-close"
                onClick={() => setSearchOpen(false)}
              >
                ×
              </button>
            </div>
            <DaumPostcode
              onComplete={handleComplete}
              autoClose={false}
              style={{ height: "450px" }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreInfo;
